import { z } from "zod/v4";

import {
  RequiredTestGroupSchema,
  TestGroupDtoSchema,
} from "../dtos/deprecated/test-groups";
import { TrafficConfigDtoSchema } from "../dtos/deprecated/traffic-configs";
import { EstimatedTestLengthRangeSchema } from "../dtos/experience-analysis";
import { ExperienceProductDtoSchema } from "../dtos/experience-products";
import {
  ExperienceCurrencyDtoSchema,
  ExperienceDevicePreviewDtoSchema,
  ExperienceDtoSchema,
  ExperienceStatusList,
  ExperienceTypeList,
} from "../dtos/experiences";

import { AnalyticsViewTypeList } from "./analytics/base";

export const PersonalizationTestTypeList = [
  "Content Edits",
  "Theme",
  "Template",
  "Price",
  "Redirects",
  "Offers",
] as const;
export type PersonalizationTestType =
  (typeof PersonalizationTestTypeList)[number];

export const PersonalizationOfferTypeList = [
  "Volume Discount",
  "Amount off Products",
  "Amount off Order",
  "Free Gift",
  "Free Shipping",
] as const;
export type PersonalizationOfferType =
  (typeof PersonalizationOfferTypeList)[number];

export const ExperienceTestTypeList = [
  "pricing",
  "shipping",
  "offer",
  "content",
] as const;
export type ExperienceTestType = (typeof ExperienceTestTypeList)[number];

export const ExperienceRolloutList = [
  "allMods",
  "noMods",
  "nonNativeModsOnly",
] as const;
export type ExperienceRolloutType = (typeof ExperienceRolloutList)[number];

export const ExperienceAnalyticStatusList = [
  "Gathering Data",
  "Ready for Analysis",
  "Complete",
] as const;
export type ExperienceAnalyticsStatus =
  (typeof ExperienceAnalyticStatusList)[number];

const GoogleAnalyticsSchema = z.object({
  customDimensionIndex: z.number().nullish(),
});
export type GoogleAnalyticsType = z.infer<typeof GoogleAnalyticsSchema>;

export const RequiredExperienceSchema = z.object({
  id: z.uuid().optional(),
  name: z.string(),
  hasTestPricing: z.boolean().default(true),
  hasTestShipping: z.boolean().default(false),
  hasTestCampaign: z.boolean().default(false),
  hasTestContent: z.boolean().default(false),
  isPreview: z.boolean().optional().default(true),
  description: z.string().nullish(),
  createdAtTs: z.number().optional(),
  startedAtTs: z.number().nullish(),
  pausedAtTs: z.number().nullish(),
  endedAtTs: z.number().nullish(),
  status: z.enum(ExperienceStatusList).nullish(),
  progress: z.number().nullish(),
  analysisUrl: z.string().nullish(),
  controlMonthly: z.number().nullish(),
  excludeFromImpact: z.boolean().default(false).optional(),
  testGroups: RequiredTestGroupSchema.array(),
  impact: z.number().nullish(),
  analyticsViewType: z.enum(AnalyticsViewTypeList).default("none").optional(),
  estimatedTestLengthRange: EstimatedTestLengthRangeSchema.default({
    low: null,
    high: null,
  }).optional(),
  impactMetric: z.string().nullish(),
  devicePreview: ExperienceDevicePreviewDtoSchema.nullish(),
  previewPath: z.string().nullish(),
  visitors: z.number().optional(),
  orders: z.number().optional(),
  testFlowType: z.enum(ExperienceTypeList),
  goal: z.string().nullish(),
  isExternal: z.boolean().default(false).nullish(),
  currency: ExperienceCurrencyDtoSchema.optional().nullish(),
});
export type RequiredExperienceType = z.infer<typeof RequiredExperienceSchema>;

export const EnsuredExperienceStateSchema = ExperienceDtoSchema.extend({
  testGroups: TestGroupDtoSchema.array(),
  trafficConfig: TrafficConfigDtoSchema,
  testProducts: ExperienceProductDtoSchema.array().default([]),
  devicePreview: ExperienceDevicePreviewDtoSchema,
  allTestProductsLoaded: z.boolean().default(false),
});

type _EnsuredExperienceStateType = z.infer<typeof EnsuredExperienceStateSchema>;

export const ExperienceStateWithTrafficConfigs = ExperienceDtoSchema.extend({
  trafficConfig: TrafficConfigDtoSchema,
});
export type ExperienceStateWithTrafficConfigType = z.infer<
  typeof ExperienceStateWithTrafficConfigs
>;

export const ProgressBarConfigSchema = z.object({
  subtotalQuerySelector: z.string(),
  defaultThreshold: z.string(),
  minimumPurchaseAmount: z.string(),
  underMinimumText: z.string(),
  underThresholdText: z.string(),
  overThresholdText: z.string(),
  maxWidth: z.string(),
  textColor: z.string(),
  textSize: z.string(),
  underMinimumBackgroundColor: z.string(),
  progressBarBackgroundColor: z.string(),
  progressBarCompletedColor: z.string(),
  progressBarHeight: z.string(),
  showWhileCartIsEmpty: z.boolean().optional(),
  enabled: z.boolean().optional(),
});
export type ProgressBarConfigType = z.infer<typeof ProgressBarConfigSchema>;
